import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { haptics } from '../utils/haptics';
import { 
    Users, 
    BarChart3, 
    Activity, 
    Database, 
    Play, 
    Check, 
    Zap, 
    RefreshCw, 
    Server, 
    Eye, 
    AlertTriangle,
    Grid,
    Calendar,
    Lock,
    Unlock,
    ShieldAlert,
    LogOut,
    EyeOff
} from 'lucide-react';

/**
 * FILE: AdminDashboard.js
 * PURPOSE: Internal operations console — traffic/telemetry KPIs (online sockets, total
 * "Ghosts", active polls, vote counts), poll type/category distribution charts, a recent-polls
 * table, and two admin actions: trigger a 100-poll database seed, and a "sync/clear cache"
 * button that just re-fetches stats (no actual cache-clear call is made client-side, see
 * handleClearCache).
 *
 * ACCESS CONTROL: This component gates itself behind its own username/password login
 * (api.adminLogin), stored as `admin_token` in sessionStorage — completely separate from the
 * normal anonymous "Ghost" identity/token used by the rest of the app. Whether that gate is
 * actually reached depends on how the caller routes here:
 *   - App.js's sidebar nav / `renderScreen()` switch case renders this only if the user's
 *     normal Ghost identity `isVerified` is true.
 *   - App.js's TOP-LEVEL `if (currentScreen === 'admin')` branch (checked before
 *     `renderScreen()` is ever called) renders <AdminDashboard> unconditionally whenever the
 *     URL path is `/admin`, with NO `isVerified` check — see audit notes. The login form
 *     below is still required to see any real data, but this makes the Ghost-verification
 *     gate inconsistent/bypassable depending on entry point.
 */
export function AdminDashboard({ darkMode }) {
    const [token, setToken] = useState(() => sessionStorage.getItem('admin_token') || '');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loginLoading, setLoginLoading] = useState(false);
    const [loginError, setLoginError] = useState(null);

    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [seeding, setSeeding] = useState(false);
    const [seedResult, setSeedResult] = useState(null);
    const [clearing, setClearing] = useState(false);

    const fetchStats = async () => {
        if (!token) return;
        try {
            setError(null);
            const data = await api.getAdminDashboardStats();
            setStats(data);
        } catch (err) {
            setError(err.message);
            if (err.message.includes('Unauthorized') || err.message.includes('Invalid admin')) {
                handleLogout();
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            setLoading(true);
            fetchStats();
            const interval = setInterval(fetchStats, 10000);
            return () => clearInterval(interval);
        }
    }, [token]);

    const handleLogin = async (e) => {
        e.preventDefault();
        haptics.impact();
        if (!username || !password) {
            setLoginError('Username and password are required.');
            return;
        }

        try {
            setLoginLoading(true);
            setLoginError(null);
            const res = await api.adminLogin(username, password);
            if (res.token) {
                sessionStorage.setItem('admin_token', res.token);
                setToken(res.token);
            } else {
                setLoginError('Authentication failed.');
            }
        } catch (err) {
            setLoginError(err.message || 'Invalid admin credentials.');
        } finally {
            setLoginLoading(false);
        }
    };

    const handleLogout = () => {
        haptics.impact();
        sessionStorage.removeItem('admin_token');
        setToken('');
        setStats(null);
    };

    const handleSeedTrigger = async () => {
        haptics.impact();
        try {
            setSeeding(true);
            setSeedResult(null);
            await api.triggerAdminSeed();
            setSeedResult('Database seeding triggered! 100 new polls are being added in the background.');
            setTimeout(fetchStats, 3000);
        } catch (err) {
            setError('Failed to trigger seeding: ' + err.message);
        } finally {
            setSeeding(false);
        }
    };

    // Despite the "Sync Ledger & Clear Cache" label, this does not call any cache-invalidation
    // endpoint — it just re-runs fetchStats() after an artificial 1.2s delay purely so the
    // spinner has something to show. Any actual cache clearing happens (or doesn't) entirely
    // server-side as a side effect of whatever fetchStats()'s endpoint does.
    const handleClearCache = async () => {
        haptics.impact();
        setClearing(true);
        setTimeout(async () => {
            await fetchStats();
            setClearing(false);
        }, 1200);
    };

    const sectionClass = `p-6 md:p-8 rounded-[32px] border transition-all duration-300 ${darkMode ? 'bg-gray-800/40 border-white/5 hover:border-white/10' : 'bg-white border-gray-100 shadow-sm hover:border-gray-200'}`;
    const cardTitleClass = `text-sm font-black uppercase italic tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`;
    const labelClass = `text-2xs font-black uppercase tracking-[0.2em] opacity-40 mb-2 block`;

    // ─── LOGIN VIEW ───
    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4 py-12 animate-in fade-in duration-500">
                <div className="relative w-full max-w-md">
                    {/* Background glow */}
                    <div className="absolute inset-0 bg-primary-600/10 blur-[80px] rounded-full"></div>
                    
                    <div className="relative bg-white dark:bg-gray-900 border border-gray-100 dark:border-white/5 rounded-[40px] shadow-2xl p-8 md:p-10 space-y-8">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-primary-600/10 rounded-2xl flex items-center justify-center mx-auto mb-4 text-primary-600">
                                <Lock size={32} />
                            </div>
                            <h2 className="text-3xl font-black uppercase italic tracking-tighter text-gray-900 dark:text-white">Admin Control Portal</h2>
                            <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mt-1">System authorization required</p>
                        </div>

                        {loginError && (
                            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3">
                                <AlertTriangle className="text-red-500 flex-shrink-0" size={18} />
                                <p className="text-red-400 font-bold text-xs">{loginError}</p>
                            </div>
                        )}

                        <form onSubmit={handleLogin} className="space-y-5">
                            <div>
                                <label className="text-2xs font-black uppercase tracking-[0.2em] text-gray-500 mb-2 block">Username</label>
                                <input 
                                    type="text" 
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    placeholder="Enter admin username"
                                    className="w-full h-12 px-4 rounded-xl bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-white/10 text-sm font-bold text-gray-900 dark:text-white focus:outline-none focus:border-primary-600 transition-colors"
                                />
                            </div>

                            <div>
                                <label className="text-2xs font-black uppercase tracking-[0.2em] text-gray-500 mb-2 block">Password</label>
                                <div className="relative">
                                    <input 
                                        type={showPassword ? "text" : "password"} 
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Enter admin password"
                                        className="w-full h-12 pl-4 pr-12 rounded-xl bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-white/10 text-sm font-bold text-gray-900 dark:text-white focus:outline-none focus:border-primary-600 transition-colors"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loginLoading}
                                className="w-full h-14 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl font-black uppercase text-xs tracking-widest transition-all hover:scale-[1.02] active:scale-95 shadow-xl shadow-purple-500/20 flex items-center justify-center gap-2 mt-8"
                            >
                                {loginLoading ? (
                                    <RefreshCw size={16} className="animate-spin" />
                                ) : (
                                    <>
                                        <Unlock size={16} />
                                        Authenticate Access
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    // ─── DASHBOARD LOAD STATE ───
    if (loading && !stats) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-950 py-32 animate-in fade-in duration-500">
                <div className="relative">
                    <div className="absolute inset-0 bg-primary-600/20 blur-[30px] rounded-full animate-pulse"></div>
                    <Server className="text-primary-600 animate-spin mb-4" size={48} />
                </div>
                <p className="text-xs font-black uppercase tracking-[0.2em] text-gray-500 mt-2">Connecting Telemetry...</p>
            </div>
        );
    }

    const totalPolls = stats ? (stats.kpis.activePolls + stats.kpis.expiredPolls + stats.kpis.incubatorPolls) : 0;

    // ─── MAIN SYSTEM DASHBOARD VIEW ───
    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 px-4 md:px-8 py-8 animate-in fade-in duration-500">
            <div className="max-w-6xl mx-auto space-y-8">
                {/* Header section */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-gray-100 dark:border-white/5 pb-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="bg-primary-600 text-white text-2xs font-black px-2.5 py-0.5 rounded-full uppercase tracking-widest italic shadow-lg shadow-purple-500/20">Telemetry V3.2</div>
                            <h2 className="text-4xl md:text-5xl font-black uppercase italic tracking-tighter leading-none text-gray-900 dark:text-white">System Dashboard</h2>
                        </div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest italic">Global network metrics and database controls.</p>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        <button 
                            onClick={fetchStats} 
                            className="px-5 py-3 bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 text-gray-700 dark:text-white rounded-2xl font-black uppercase text-2xs tracking-widest transition-all hover:scale-105 active:scale-95 flex items-center gap-2"
                        >
                            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
                            Force Sync
                        </button>
                        
                        <button 
                            onClick={handleLogout} 
                            className="px-5 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-2xl font-black uppercase text-2xs tracking-widest transition-all hover:scale-105 active:scale-95 flex items-center gap-2"
                        >
                            <LogOut size={12} />
                            Logout
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3">
                        <AlertTriangle className="text-red-500 flex-shrink-0" size={20} />
                        <p className="text-red-400 font-bold text-xs">{error}</p>
                    </div>
                )}

                {seedResult && (
                    <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-2xl flex items-center gap-3">
                        <Check className="text-green-500 flex-shrink-0" size={20} />
                        <p className="text-green-400 font-bold text-xs">{seedResult}</p>
                    </div>
                )}

                {/* Top KPI Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Active Citizens */}
                    <div className={`${sectionClass} relative overflow-hidden group`}>
                        <div className="absolute top-4 right-4 text-emerald-500 group-hover:scale-110 transition-transform">
                            <Activity size={20} className="animate-pulse" />
                        </div>
                        <span className={labelClass}>Live Sockets</span>
                        <div className="text-4xl font-black italic tracking-tighter text-emerald-500">
                            {stats?.kpis.onlineUsers || 1}
                        </div>
                        <span className="text-2xs font-bold text-gray-500 uppercase tracking-widest block mt-2">Active Citizens</span>
                    </div>

                    {/* Registered Ghosts */}
                    <div className={`${sectionClass} relative overflow-hidden group`}>
                        <div className="absolute top-4 right-4 text-purple-500 group-hover:scale-110 transition-transform">
                            <Users size={20} />
                        </div>
                        <span className={labelClass}>Total Ghosts</span>
                        <div className="text-4xl font-black italic tracking-tighter text-purple-500">
                            {stats?.kpis.totalGhosts || 0}
                        </div>
                        <span className="text-2xs font-bold text-gray-500 uppercase tracking-widest block mt-2">Identity Records</span>
                    </div>

                    {/* Active Polls */}
                    <div className={`${sectionClass} relative overflow-hidden group`}>
                        <div className="absolute top-4 right-4 text-primary-600 group-hover:scale-110 transition-transform">
                            <Zap size={20} />
                        </div>
                        <span className={labelClass}>Active Polls</span>
                        <div className="text-4xl font-black italic tracking-tighter text-primary-600">
                            {stats?.kpis.activePolls || 0}
                        </div>
                        <span className="text-2xs font-bold text-gray-500 uppercase tracking-widest block mt-2">Unexpired public</span>
                    </div>

                    {/* Total Votes */}
                    <div className={`${sectionClass} relative overflow-hidden group`}>
                        <div className="absolute top-4 right-4 text-amber-500 group-hover:scale-110 transition-transform">
                            <Database size={20} />
                        </div>
                        <span className={labelClass}>Votes Ledger</span>
                        <div className="text-4xl font-black italic tracking-tighter text-amber-500">
                            {stats?.kpis.totalVotes.toLocaleString() || 0}
                        </div>
                        <span className="text-2xs font-bold text-gray-500 uppercase tracking-widest block mt-2">Encrypted Records</span>
                    </div>
                </div>

                {/* Polls & Category Distributions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Poll Types */}
                    <div className={sectionClass}>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2.5 bg-primary-600/10 rounded-xl text-primary-600">
                                <BarChart3 size={18} />
                            </div>
                            <h3 className={cardTitleClass}>Poll Type Distribution</h3>
                        </div>
                        
                        <div className="space-y-4">
                            {['single', 'multiple', 'rating', 'rank', 'binary'].map(type => {
                                const count = stats?.typeDistribution[type] || 0;
                                const percentage = totalPolls > 0 ? ((count / totalPolls) * 100) : 0;
                                return (
                                    <div key={type} className="space-y-1.5">
                                        <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                                            <span className="italic">{type === 'single' ? 'Single Choice' : type === 'multiple' ? 'Multiple Choice' : type}</span>
                                            <span className="tabular-nums font-mono opacity-80">{count} ({percentage.toFixed(0)}%)</span>
                                        </div>
                                        <div className="h-2 w-full bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
                                            <div 
                                                className="h-full bg-gradient-to-r from-primary-600 to-purple-500 rounded-full transition-all duration-1000"
                                                style={{ width: `${percentage}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Categories */}
                    <div className={sectionClass}>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2.5 bg-amber-500/10 rounded-xl text-amber-500">
                                <Grid size={18} />
                            </div>
                            <h3 className={cardTitleClass}>Category Breakdown</h3>
                        </div>

                        <div className="grid grid-cols-2 gap-4 max-h-[220px] overflow-y-auto pr-2 no-scrollbar">
                            {Object.entries(stats?.categoryDistribution || {}).sort((a, b) => b[1] - a[1]).map(([cat, count]) => {
                                const pct = totalPolls > 0 ? ((count / totalPolls) * 100) : 0;
                                return (
                                    <div key={cat} className="p-3 bg-gray-50/50 dark:bg-white/[0.02] border border-gray-100 dark:border-white/5 rounded-2xl flex flex-col justify-between">
                                        <span className="text-2xs font-black uppercase tracking-wider text-gray-500 truncate">{cat}</span>
                                        <div className="flex items-baseline justify-between mt-2">
                                            <span className="text-lg font-black italic text-gray-900 dark:text-white">{count}</span>
                                            <span className="text-2xs font-mono font-bold text-primary-600">{pct.toFixed(0)}%</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Interactive Control Console */}
                <div className={sectionClass}>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2.5 bg-blue-500/10 rounded-xl text-blue-500">
                            <Server size={18} />
                        </div>
                        <h3 className={cardTitleClass}>Control Console & System Commands</h3>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <button
                            onClick={handleSeedTrigger}
                            disabled={seeding}
                            className="flex-1 px-6 py-4 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white rounded-2xl font-black uppercase text-2xs tracking-widest transition-all hover:scale-[1.02] active:scale-95 shadow-lg shadow-purple-500/20 flex items-center justify-center gap-2"
                        >
                            {seeding ? (
                                <RefreshCw size={14} className="animate-spin" />
                            ) : (
                                <Play size={14} />
                            )}
                            Seed 100 Realistic Polls
                        </button>

                        <button
                            onClick={handleClearCache}
                            disabled={clearing}
                            className="flex-1 px-6 py-4 bg-white dark:bg-white/[0.04] disabled:opacity-60 text-gray-700 dark:text-white rounded-2xl font-black uppercase text-2xs tracking-widest transition-all hover:scale-[1.02] active:scale-95 border border-gray-200 dark:border-white/10 flex items-center justify-center gap-2"
                        >
                            {clearing ? (
                                <RefreshCw size={14} className="animate-spin" />
                            ) : (
                                <RefreshCw size={14} />
                            )}
                            Sync Ledger & Clear Cache
                        </button>
                    </div>
                </div>

                {/* Recent Polls List */}
                <div className={sectionClass}>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2.5 bg-emerald-500/10 rounded-xl text-emerald-500">
                            <Calendar size={18} />
                        </div>
                        <h3 className={cardTitleClass}>Recent Polls Logs</h3>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-gray-100 dark:border-white/5 text-2xs font-black uppercase tracking-[0.2em] text-gray-500">
                                    <th className="pb-3">Question</th>
                                    <th className="pb-3 text-center">Category</th>
                                    <th className="pb-3 text-center">Type</th>
                                    <th className="pb-3 text-center">Status</th>
                                    <th className="pb-3 text-right">Votes</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-white/5 text-xs">
                                {stats?.recentPolls.map(p => (
                                    <tr key={p.id} className="group hover:bg-gray-50/50 dark:hover:bg-white/[0.01]">
                                        <td className="py-4 font-bold text-gray-900 dark:text-white pr-4 max-w-[240px] truncate italic">
                                            {p.question}
                                        </td>
                                        <td className="py-4 text-center">
                                            <span className="px-2.5 py-0.5 bg-gray-100 dark:bg-white/5 rounded-full text-2xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                                {p.category}
                                            </span>
                                        </td>
                                        <td className="py-4 text-center font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest text-2xs italic">
                                            {p.poll_type}
                                        </td>
                                        <td className="py-4 text-center">
                                            <span className={`px-2 py-0.5 rounded text-2xs font-black uppercase tracking-widest ${
                                                p.status === 'public' ? 'bg-green-500/15 text-green-500' : 'bg-amber-500/15 text-amber-500'
                                            }`}>
                                                {p.status}
                                            </span>
                                        </td>
                                        <td className="py-4 text-right font-bold tabular-nums text-gray-900 dark:text-white">
                                            {p.total_voters || 0}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
